import { useRef, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Container } from '@/components/layout/Container';
import { Section } from '@/components/layout/Section';
import { Eyebrow } from '@/components/domain/Eyebrow';
import { useFadeUpOnScroll } from '@/hooks/useFadeUpOnScroll';
import styles from './CapabilitiesSection.module.css';

interface Capability {
  name: string;
  icon: string;
  /** 'brand' = blue top stripe; 'trust' = green. */
  tone: 'brand' | 'trust';
}

const CAPABILITIES: ReadonlyArray<Capability> = [
  { name: 'NovarPass', icon: '🪪', tone: 'brand' },
  { name: 'NovarVault', icon: '🔐', tone: 'brand' },
  { name: 'NovarConsent', icon: '⚡', tone: 'brand' },
  { name: 'NovarAnchor', icon: '⚓', tone: 'trust' },
  { name: 'NovarAIScan', icon: '🔍', tone: 'trust' },
  { name: 'NovarLCM', icon: '📊', tone: 'trust' },
];

export const CapabilitiesSection: FC = () => {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  useFadeUpOnScroll(ref, { selector: '[data-fade]', stagger: 0.06 });

  const items = t('home.capabilities.items', { returnObjects: true }) as Array<{
    title: string;
  }>;

  return (
    <Section tone="default" id="capabilities">
      <Container size="wide">
        <div ref={ref} className={styles.root}>
          <header className={styles.header}>
            <div data-fade>
              <Eyebrow>{t('home.capabilities.kicker')}</Eyebrow>
            </div>
            <h2 className={styles.title} data-fade>
              {t('home.capabilities.title')}
            </h2>
            <p className={styles.lede} data-fade>
              {t('home.capabilities.lede')}
            </p>
          </header>

          <ul className={styles.grid}>
            {CAPABILITIES.map((c, idx) => {
              const subtitle = items[idx]?.title ?? '';
              return (
                <li
                  key={c.name}
                  className={[styles.card, styles[`card_${c.tone}`]].join(' ')}
                  data-fade
                >
                  <span className={styles.stripe} aria-hidden="true" />
                  <div className={styles.cardHead}>
                    <span className={styles.icon} aria-hidden="true">
                      {c.icon}
                    </span>
                    <span className={styles.name}>{c.name}</span>
                  </div>
                  <p className={styles.subtitle}>{subtitle}</p>
                </li>
              );
            })}
          </ul>

          <div className={styles.audit} data-fade>
            <span className={styles.auditBadge}>
              <span className={styles.auditIcon} aria-hidden="true">
                ✅
              </span>
              <span className={styles.auditName}>NovarAudit</span>
            </span>
            <span className={styles.auditBody}>{t('home.capabilities.auditBody')}</span>
          </div>
        </div>
      </Container>
    </Section>
  );
};

CapabilitiesSection.displayName = 'CapabilitiesSection';
